




















































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import smoothReflow from 'vue-smooth-reflow';
import Multiselect from 'vue-multiselect';
import OrderProductVariant from '@/zenky/interfaces/orders/OrderProductVariant';
import FormFieldOption from '@/zenky/forms/interfaces/FormFieldOption';
import axios from '@/zenky/api/Http';
import axiosModule from 'axios';
import PaginatedResponse from '@/zenky/api/responses/PaginatedResponse';
import Product from '@/zenky/interfaces/products/Product';
import ProductVariant from '@/zenky/interfaces/products/ProductVariant';
import { variantOptionColor } from '@/helpers/utils';
import ProductVariantRemainder from '@/zenky/interfaces/products/ProductVariantRemainder';
import VariantOptionValue from '@/zenky/interfaces/products/VariantOptionValue';
import { ChevronDownIcon } from 'vue-feather-icons';

const deliberateCancelMessage = 'noLongerNeeded';
// Taking into account "includes" that we specified.
type ResponseProductVariant = ProductVariant & {
  remainders: ProductVariantRemainder[],
  option_values: VariantOptionValue[], // eslint-disable-line camelcase
};
interface ResponseProduct extends Product {
  variants: ResponseProductVariant[];
}

interface MultiselectOption {
  type: 'variant' | 'product-w-one-variant' | 'product-w-many-variants';
  item: ResponseProduct | ResponseProductVariant;
}
interface VariantOption extends MultiselectOption {
  type: 'variant';
  item: ResponseProductVariant;
  parent: ResponseProduct;
}
interface ProductWOneVariantOption extends MultiselectOption {
  type: 'product-w-one-variant';
  item: ResponseProduct & { variants: [ResponseProductVariant]};
}
interface ProductWManyVariantsOption extends MultiselectOption {
  type: 'product-w-many-variants';
  item: ResponseProduct;
}

export type VariantClickEvent = ResponseProductVariant;

@Component({
  components: { Multiselect, ChevronDownIcon },
  mixins: [smoothReflow],
})
export default class ProductSearch extends Vue {
  @Prop({ required: true }) readonly variantCounts!: { [variantId: string]: number } | null;
  // Must be set to `null` when there's no request pending.
  requestCancelTokenSource: ReturnType<typeof axiosModule['CancelToken']['source']> | null = null;
  requestTimeout: number | null = null; // Must be set to `null` when there's no timeout pending.
  term: string = '';
  response: ResponseProduct[] = [];
  getVariantOptionColor = variantOptionColor;

  mounted() {
    // Smooth reflow is not activated on dropdown open-close because it works hooks like `beforeUpdate`.
    // But it does not harm us.
    this.$smoothReflow!({
      el: '.multiselect .multiselect__content-wrapper',
      transition: 'height 0.18s ease',
      hideOverflow: false,
    });
  }

  async search() {
    const params = { search: this.term, with: ['variants.option_values', 'variants.remainders'], count: 20 };
    if (this.requestCancelTokenSource !== null) {
      throw new Error('There must be at most one request at a time, and'
        + 'when there is no pending request, `this.requestCancelTokenSource` must be `null`');
    }
    this.requestCancelTokenSource = axiosModule.CancelToken.source();
    const request = axios.get('products', { params, cancelToken: this.requestCancelTokenSource.token });
    try {
      const products = ((await request).data as PaginatedResponse<ResponseProduct>).data;
      this.response = products;
    } catch (err) {
      if (err.message !== deliberateCancelMessage) {
        throw err;
      }
    } finally {
      this.requestCancelTokenSource = null;
    }
  }
  onTermChange(term: string) {
    this.term = term;
    clearTimeout(this.requestTimeout as any); // Will do nothing if it's `null`.
    this.requestTimeout = null;
    if (this.requestCancelTokenSource !== null) {
      this.requestCancelTokenSource.cancel(deliberateCancelMessage);
      this.requestCancelTokenSource = null;
    }
    this.response = [];
    if (!this.termTooShort) {
      this.requestTimeout = setTimeout(() => { this.search(); this.requestTimeout = null; }, 250);
    }
  }
  get loading(): boolean {
    return this.requestTimeout !== null || this.requestCancelTokenSource !== null;
  }
  get termTooShort(): boolean {
    return this.term.length < 2;
  }

  get multiselectOptions(): Array<ProductWManyVariantsOption | ProductWOneVariantOption | VariantOption> {
    const options: this['multiselectOptions'] = [];
    this.response.forEach((product) => {
      if (product.variants.length > 1) {
        if (product.variants.some(variant => variant.option_values.length === 0)) {
          // Ignore those buggy ones. TODO do we still need to?
          return;
        }
        options.push({ item: product, type: 'product-w-many-variants' });
        product.variants.forEach(variant => options.push({ type: 'variant', item: variant, parent: product }));
      } else if (product.variants.length === 1) {
        options.push({ item: product as ProductWOneVariantOption['item'], type: 'product-w-one-variant' });
      }
      // Ignore those with no variants.
    });
    return options;
  }

  // Makes it easier to write the template for the case when we have a product with one variant or a variant itself.
  getVariant(
    variantOrProductWOneVariant: this['multiselectOptions'][number] & VariantOption | ProductWOneVariantOption,
  ): ResponseProductVariant {
    if (variantOrProductWOneVariant.type === 'variant') {
      return variantOrProductWOneVariant.item;
    }
    return variantOrProductWOneVariant.item.variants[0];
  }
  getVariantCartCount(searchVariant: ResponseProductVariant): number {
    if (this.variantCounts === null) {
      return 0;
    }
    const dictVal = this.variantCounts[searchVariant.id];
    if (dictVal === undefined) {
      return 0;
    }
    return dictVal;
  }
  getVariantRemainder(searchVariant: ResponseProductVariant): number {
    return searchVariant.remainders.reduce((sum, currRemainder) => sum + currRemainder.quantity, 0);
  }
  getUnit(option: this['multiselectOptions'][number] & VariantOption | ProductWOneVariantOption): string {
    let product: ResponseProduct;
    if (option.type === 'product-w-one-variant') {
      product = option.item;
    } else if (option.type === 'variant') {
      product = option.parent;
    } else { throw new Error('Unrecognized item type'); }
    switch (product.unit_type.id) {
      case 'piece': return 'шт';
      case 'gram': return 'г';
      case 'kilogram': return 'кг';
      case 'meter': return 'м';
      default: {
        console.log('Unrecognized unit type', product.unit_type.id);
        return '';
      }
    }
  }

  handleSelect($event: this['multiselectOptions'][number]) {
    if ($event.type === 'product-w-many-variants') {
      return;
    }
    let variant: ResponseProductVariant;
    if ($event.type === 'product-w-one-variant') {
      [variant] = ($event as ProductWOneVariantOption).item.variants;
    } else if ($event.type === 'variant') {
      variant = ($event as VariantOption).item;
    } else { throw new Error('Unrecognized item type'); }
    this.$emit('variant-click', variant);
  }
}

const TextWHighlightedPart = Vue.component('TextWHighlightedPart', {
  functional: true,
  props: {
    toHighlight: {
      type: String,
      required: true,
    },
  },
  render: (createElement, context) => {
    if (context.children.length !== 1 || typeof context.children[0].text !== 'string') {
      throw new Error('This component only accepts text as a child');
    }
    const innerText = context.children[0].text;
    const elements = [];
    let unconvertedStart = 0;
    let stopLoop: boolean = false;
    while (!stopLoop) {
      const hlStart = innerText.toLowerCase().indexOf(context.props.toHighlight.toLowerCase(), unconvertedStart);
      if (hlStart === -1) {
        stopLoop = true;
      } else {
        const hlEnd = hlStart + context.props.toHighlight.length;
        elements.push(innerText.slice(unconvertedStart, hlStart));
        elements.push(createElement('b', innerText.slice(hlStart, hlEnd)));
        unconvertedStart = hlEnd;
      }
    }
    elements.push(innerText.slice(unconvertedStart));
    return createElement('span', elements);
  },
});
